import { Button, Flex, Icon } from "@chakra-ui/react";
import { t } from "i18next";
import { cloneDeep } from "lodash";
import { useCallback, useState } from "react";
import { FaCheck, FaPlus } from "react-icons/fa";
import {
  DefaultNotificationSettingsDto,
  OrganizationRoleType,
  SupplierNotificationReceiversDto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { MultiSelector } from "../../../common/input/Selector/MultiSelector";
import { PersonsSelector } from "../../../common/persons/PersonsSelector";
import { useApiError } from "../../../common/errors/useApiError";
import { useSaveOrganization } from "../../organizations/current/useOrganizationEditor";
import { organizationSourcingRoles } from "../../organizations/current/manage-users/utils";
import { initSettings } from "./NotificationSettings";
import { useReceiverRoleNames } from "./useReceiverRoleNames";

type ReceiverType = "inviteReceivers" | "messageReceivers";

const initReceivers = (): SupplierNotificationReceiversDto => ({
  persons: [],
  organizationRoles: [],
});

export const SupplierNotificationReceiverForm = ({ receiverType }: { receiverType: ReceiverType }) => {
  const roleName = useReceiverRoleNames();
  const authState = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();

  const { saveOrganization, isSaving } = useSaveOrganization();

  const [isVisible, setIsVisible] = useState(false);
  const [receivers, setReceivers] = useState<SupplierNotificationReceiversDto>(initReceivers());

  const saveReceivers = useCallback(async () => {
    const settings: DefaultNotificationSettingsDto =
      cloneDeep(authState.selectedOrg.defaultNotificationSettings) ?? initSettings();
    if (!settings.supplierNotificationSettings) {
      settings.supplierNotificationSettings = {
        inviteReceivers: initReceivers(),
        messageReceivers: initReceivers(),
      };
    }
    settings.supplierNotificationSettings[receiverType] = receivers;
    try {
      await saveOrganization({ defaultNotificationSettings: settings });
      setReceivers(initReceivers());
      setIsVisible(false);
    } catch (e) {
      trigger(e);
    }
  }, [authState.selectedOrg.defaultNotificationSettings, trigger, receivers, receiverType, saveOrganization]);

  const currentReceivers =
    authState.selectedOrg.defaultNotificationSettings?.supplierNotificationSettings?.[receiverType];

  if (currentReceivers && (currentReceivers.persons.length > 0 || currentReceivers.organizationRoles.length > 0)) {
    return null;
  }

  if (!isVisible)
    return (
      <Flex>
        <Button
          rightIcon={<Icon as={FaPlus} />}
          size="xs"
          variant={"ghost"}
          colorScheme="teal"
          onClick={() => setIsVisible(true)}
        >
          {t("Configure")}
        </Button>
      </Flex>
    );

  return (
    <Flex
      position="relative"
      zIndex="2"
      flexDirection="column"
      rowGap="2"
      border="1px solid"
      borderColor="smBorder"
      rounded="md"
      p="2"
    >
      <PersonsSelector
        size="md"
        organizationIds={[authState.selectedOrg.id]}
        selectedPersons={receivers.persons}
        roles={organizationSourcingRoles}
        onChange={(persons) => {
          const updated = cloneDeep(receivers);
          updated.persons = persons;
          setReceivers(updated);
        }}
        placeholder={`${t("Select individual receivers")}...`}
      />
      <MultiSelector
        value={receivers.organizationRoles.map((role) => ({ label: roleName.organizationRole(role), value: role }))}
        options={organizationSourcingRoles.map((role) => ({
          label: roleName.organizationRole(role),
          value: role,
        }))}
        noOptionsAvailableMessage={`${t("No roles found")}...`}
        onChange={(options) => {
          const updated = cloneDeep(receivers);
          updated.organizationRoles = options.map((o) => o.value as OrganizationRoleType);
          setReceivers(updated);
        }}
        placeholder={{ text: `${t("Select receivers by role in organization")}...`, color: "" }}
      />
      <Flex>
        <Button
          colorScheme="teal"
          size={"xs"}
          rightIcon={<Icon as={FaCheck} />}
          isLoading={isSaving}
          onClick={() => saveReceivers()}
        >
          {t("Save")}
        </Button>
      </Flex>
    </Flex>
  );
};
